import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import SearchSection from "../../../components/search/SearchSection";
import {CodeSpecNum, ContentCategory} from "../../../constant/CodeSpecNum";
import Search from "../../../components/search/Search";
import {RENTAL_REQUEST_ADMIN_HEADER} from "../../../components/table/header/Headers";
import {handleActiveError, handleActiveToast} from "../../../components/sonner/ToastBasic";
import Selector from "../../../components/selector/Selector";
import {searchListener, searchWithChildren} from "../../../components/commonmethod/simmsDrawerMethod";
import {removeRentalRequest} from "../../../api/lifecycle/rental/rentalRequestService";
import {SearchProgressComboBox} from "../../../components/select/SearchProgressComboBox";
import {getIndividualSetting, resetIndividualSetting, setIndividualSetting} from "../../../api/user/UserAccountService";
import {extractObject, initHeaderOrders, nonObject, simmsColumnsToJsGridHeaders} from "../../../components/commonmethod/CommonMethod";
import SimmsInputSearch from "../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../components/drawer/simms/SimmsDrawerSearch";
import {ToolbarDataTransfer} from "@bavuchoko/js-grid";
import SimmsJsGrid from "../../../components/js-grid/SimmsJsGrid";
import JsGridExcelGridOpenButton from "../../../components/js-grid/JsGridExcelGridOpenButton";
import {useNavigate} from "react-router-dom";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../components/pageTabs/pageTabsStorage";

const RentalRequestAdminListType = ({data, date, pageInfo, dataHook}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {handleSearch, handlePageSizeChange, saveConditions, refresh, handleGridPageChange} = dataHook;

    const handleRefresh = async () => {
        await refresh();
    }

    const [header, setHeader] = useState({order: RENTAL_REQUEST_ADMIN_HEADER(handleRefresh), exclude: []});
    const [exclude, setExclude] = useState([]);
    const [, setPin] = useState();

    const searchDate = {startDate: saveConditions.startDate ?? date.startDate, endDate: saveConditions.endDate ?? date.endDate}

    useEffect(() => {
        getIndividualSetting().then(r => {
            initHeaderOrders(r, RENTAL_REQUEST_ADMIN_HEADER(handleRefresh), setHeader, exclude, setExclude, setPin, 'rental_admin_v2');
        })
    }, [])

    const handleRowClick = (row) => {
        const id = row?.id;
        if (id == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, "대여신청 상세"),
            url: `/lifecycle/rentalRequestAdmin/${id}`,
        });
    };

    const deleteRentalRequest = async (rows) => {
        if (!rows?.length) {
            handleActiveError({message: t(`notSelected`)});
            return;
        }
        if (!window.confirm(t(`confirmRentalRequestRemove`))) {
            return;
        }
        const ids = rows.map((row) => row.id).filter((id) => id != null);
        await removeRentalRequest({ids}, () => {
            handleActiveToast({message: t(`removed`)});
            refresh();
        });
    }

    const rentalRequestJsGridHeader = useMemo(() => simmsColumnsToJsGridHeaders(header), [header]);
    const excelGridSearch = useMemo(() => extractObject(saveConditions), [saveConditions]);

    return (
        <div className="container-height">
            <Search refresh={refresh} entity={'rental_admin'} searchConditions={saveConditions} handleSearch={handleSearch} handlePageSizeChange={handlePageSizeChange} pageInfo={pageInfo}
                    searchBar={{ placeHolder : '대여 자산명'}} size detail date={searchDate}>
                <SearchSection>
                    <Selector showLabel onChange={(value) => searchWithChildren(handleSearch, 'rentalAssetCode', extractObject(saveConditions), value) }
                                 data={saveConditions.rentalAssetCode}
                                 parentKey={CodeSpecNum.RentalAssetCode} recursive
                                 entity={"code"} title={"대여자산그룹"}/>
                    <SearchProgressComboBox title={"신청상태"} target={"rentalType"} tableName={ContentCategory.RENTAL_ASSET} value={saveConditions}
                                            onChange={(v) => nonObject(saveConditions, v, handleSearch, "rentalType")}
                    />
                    <Selector showLabel
                                 onChange={(value) => searchListener(handleSearch, 'rentUser', extractObject(saveConditions), value)}
                                 data={saveConditions.rentUser}
                                 uniqueKey={"rentUser"}
                                 title={"대여자"} entity="userAccount">
                        <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                        <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                        <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                    </Selector>
                    <Selector showLabel
                                 onChange={(value) => searchListener(handleSearch, 'rentApprover', extractObject(saveConditions), value)}
                                 data={saveConditions.rentApprover}
                                 uniqueKey={"rentApprover"}
                                 title={"대여 승인자"} entity="userAccount">
                        <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                        <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                        <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                    </Selector>
                </SearchSection>
            </Search>

            <div className="container-height-body">
                <SimmsJsGrid
                    loading={dataHook.loading}
                    resizable={true}
                    data={data}
                    theme={'linear'}
                    style={{minHeight: 0, height: '100%'}}
                    header={rentalRequestJsGridHeader}
                    onHeaderSave={(v) => setIndividualSetting({headers: {'rental_admin_v2': v}})}
                    onHeaderReset={() => resetIndividualSetting('headers', 'rental_admin_v2')}
                    enableRowSelection={true}
                    onRowClick={handleRowClick}
                    onPageChange={handleGridPageChange}
                    toolbarEnd={() => (
                        <div className="flex items-center gap-2">
                            <JsGridExcelGridOpenButton entityName="RentalRequestAdmin" search={excelGridSearch} />
                            <ToolbarDataTransfer
                                hint="선택 항목 삭제"
                                busyHint="삭제 중…"
                                overlayLabel="삭제 중…"
                                onTransfer={deleteRentalRequest}
                            >
                                {(ctx) => (
                                    <button
                                        type="button"
                                        className="text-[13px] px-3 py-0.5 text-black rounded-4 border disabled:cursor-not-allowed"
                                        onClick={() => {
                                            if (ctx.selectedCount === 0) {
                                                handleActiveError({message: t(`notSelected`)});
                                                return;
                                            }
                                            void ctx.transferSelected();
                                        }}
                                    >
                                        삭제
                                    </button>
                                )}
                            </ToolbarDataTransfer>
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default RentalRequestAdminListType;
